import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Globe, Check } from "lucide-react";

const LANGUAGES = [
  { code: "en", label: "English",    flag: "🇬🇧" },
  { code: "fr", label: "Français",   flag: "🇫🇷" },
  { code: "zh", label: "中文",        flag: "🇨🇳" },
  { code: "de", label: "Deutsch",    flag: "🇩🇪" },
  { code: "ru", label: "Русский",    flag: "🇷🇺" },
];

interface Props {
  /** Compact mode: show only globe icon (no label), suitable for dense headers */
  compact?: boolean;
}

export const LanguageSelector = ({ compact = false }: Props) => {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const current = LANGUAGES.find((l) => l.code === i18n.language) ?? LANGUAGES[0];

  const select = (code: string) => {
    i18n.changeLanguage(code);
    localStorage.setItem("te_portal_lang", code);
    setOpen(false);
  };

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-secondary/80 transition-colors"
        title="Change language"
      >
        <Globe className="h-4 w-4" />
        {!compact && (
          <span className="hidden sm:inline font-medium">{current.flag} {current.label}</span>
        )}
        {compact && <span className="text-base">{current.flag}</span>}
      </button>

      {open && (
        <div className="absolute right-0 mt-1 w-44 bg-popover border border-border rounded-xl shadow-lg z-50 overflow-hidden">
          {LANGUAGES.map((lang) => (
            <button
              key={lang.code}
              onClick={() => select(lang.code)}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-accent/10 transition-colors"
            >
              <span className="text-base">{lang.flag}</span>
              <span className={`flex-1 text-left ${lang.code === i18n.language ? "font-semibold text-foreground" : "text-muted-foreground"}`}>
                {lang.label}
              </span>
              {lang.code === i18n.language && (
                <Check className="h-3.5 w-3.5 text-accent" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
